import { format } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  HEADER_HEIGHT_PX,
  ROW_HEIGHT_PX,
  STATUS_STYLES,
  WEEK_HEADER_HEIGHT_PX,
  type MilestoneRow,
  type WeekSegment,
} from './rfp-timeline-types';
import { formatMilestoneDate, positionPercent } from './rfp-timeline-utils';

const TOTAL_HEADER_HEIGHT_PX = HEADER_HEIGHT_PX + WEEK_HEADER_HEIGHT_PX;

// 10px dash / 6px gap; color matches the `--color-day-light-1` token at 60% opacity.
// Inlined as a gradient because Tailwind's `border-dotted` can't render thin vertical
// dotted rules at sub-pixel widths reliably.
const WEEK_TICK_DOTTED_BG =
  'repeating-linear-gradient(to bottom, rgb(203 213 225 / 0.6) 0 10px, transparent 10px 16px)';

// --- MilestoneTable ---

interface MilestoneTableProps {
  rows: MilestoneRow[];
  t: (key: string) => string;
}

const MilestoneTable: FC<MilestoneTableProps> = ({ rows, t }) => (
  <div className="flex-shrink-0">
    <div
      className="grid grid-cols-[minmax(180px,1fr)_90px_110px] items-center gap-x-4 px-4 text-xs uppercase tracking-wide text-day-neutral-subtle border-b border-day-light-2"
      style={{ height: TOTAL_HEADER_HEIGHT_PX }}
    >
      <span>{t('dashboard.rfp-timeline.columns.milestone')}</span>
      <span>{t('dashboard.rfp-timeline.columns.date')}</span>
      <span>{t('dashboard.rfp-timeline.columns.status')}</span>
    </div>
    {rows.map((row) => (
      <div
        key={row.id}
        className="grid grid-cols-[minmax(180px,1fr)_90px_110px] items-center gap-x-4 px-4 border-b border-day-light-2 last:border-b-0"
        style={{ height: ROW_HEIGHT_PX }}
      >
        <span className="text-sm text-day-neutral-dark truncate" title={row.milestone}>
          {row.milestone}
        </span>
        <span className="text-sm text-day-neutral-subtle">{formatMilestoneDate(row.date)}</span>
        <span>
          <span
            className={`inline-block rounded-full px-3 py-0.5 text-xs font-medium ${STATUS_STYLES[row.status].badge}`}
          >
            {t(`dashboard.rfp-timeline.${row.status}`)}
          </span>
        </span>
      </div>
    ))}
  </div>
);

// --- TimelineMonthHeader ---

interface TimelineMonthHeaderProps {
  monthTicks: Date[];
  domain: { start: Date; end: Date };
  spansMultipleYears: boolean;
}

const TimelineMonthHeader: FC<TimelineMonthHeaderProps> = ({
  monthTicks,
  domain,
  spansMultipleYears,
}) => (
  <div className="relative border-b border-day-light-2" style={{ height: HEADER_HEIGHT_PX }}>
    {monthTicks.map((tick, index) => {
      const nextTick = monthTicks[index + 1] ?? domain.end;
      const leftPct = positionPercent(tick, domain.start, domain.end);
      const widthPct = positionPercent(nextTick, domain.start, domain.end) - leftPct;
      const showYear = spansMultipleYears ? index === 0 || tick.getMonth() === 0 : true;
      return (
        <div
          key={tick.toISOString()}
          className="absolute top-0 h-full flex items-center justify-center overflow-hidden text-xs text-day-neutral-subtle border-r border-day-light-2"
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        >
          <span className="truncate px-1">{format(tick, showYear ? 'MMM yyyy' : 'MMM')}</span>
        </div>
      );
    })}
  </div>
);

// --- TimelineWeekHeader ---

interface TimelineWeekHeaderProps {
  weekSegments: WeekSegment[];
  domain: { start: Date; end: Date };
}

const TimelineWeekHeader: FC<TimelineWeekHeaderProps> = ({ weekSegments, domain }) => {
  const { t } = useTranslation();
  return (
    <div
      className="relative border-b border-day-light-2 bg-day-light-3/40"
      style={{ height: WEEK_HEADER_HEIGHT_PX }}
    >
      {weekSegments.map((segment) => {
        const leftPct = positionPercent(segment.start, domain.start, domain.end);
        const widthPct = positionPercent(segment.end, domain.start, domain.end) - leftPct;
        return (
          <div
            key={`week-seg-${segment.start.toISOString()}`}
            className="absolute top-0 h-full flex items-center justify-center overflow-hidden text-[10px] text-day-neutral-subtle"
            style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
            title={t('dashboard.rfp-timeline.week-number', {
              weekNumber: segment.weekNumber,
            })}
          >
            <span className="truncate">W{segment.weekNumber}</span>
          </div>
        );
      })}
    </div>
  );
};

// --- TimelineBarRow ---

interface TimelineBarRowProps {
  row: MilestoneRow;
  domain: { start: Date; end: Date };
  monthTicks: Date[];
  weekTicks: Date[];
}

const TimelineBarRow: FC<TimelineBarRowProps> = ({ row, domain, monthTicks, weekTicks }) => {
  const leftPct = positionPercent(row.date, domain.start, domain.end);
  return (
    <div
      className="relative border-b border-day-light-2 last:border-b-0"
      style={{ height: ROW_HEIGHT_PX }}
    >
      {weekTicks.map((tick) => (
        <div
          key={`week-${tick.toISOString()}`}
          className="pointer-events-none absolute top-0 bottom-0 w-[2px]"
          style={{
            left: `${positionPercent(tick, domain.start, domain.end)}%`,
            backgroundImage: WEEK_TICK_DOTTED_BG,
          }}
          aria-hidden
        />
      ))}
      {monthTicks.map((tick, tickIndex) => {
        if (tickIndex === 0) return null;
        return (
          <div
            key={tick.toISOString()}
            className="absolute top-0 bottom-0 border-l border-day-light-2/60"
            style={{ left: `${positionPercent(tick, domain.start, domain.end)}%` }}
            aria-hidden
          />
        );
      })}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group cursor-pointer"
        style={{ left: `${leftPct}%` }}
      >
        <div
          className={`rotate-45 h-3 w-3 ${STATUS_STYLES[row.status].dot}`}
          aria-label={`${row.milestone} ${format(row.date, 'yyyy-MM-dd')}`}
        />
        <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden -translate-x-1/2 group-hover:block">
          <div className="whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white">
            {format(row.date, 'd MMM yyyy')}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- TimelineBars ---

interface TimelineBarsProps {
  rows: MilestoneRow[];
  domain: { start: Date; end: Date };
  monthTicks: Date[];
  weekTicks: Date[];
  weekSegments: WeekSegment[];
  spansMultipleYears: boolean;
  timelineMinWidth: number;
  todayPct: number;
}

const TimelineBars: FC<TimelineBarsProps> = ({
  rows,
  domain,
  monthTicks,
  weekTicks,
  weekSegments,
  spansMultipleYears,
  timelineMinWidth,
  todayPct,
}) => (
  <div
    className="relative flex-1 border-l border-day-light-2"
    style={{ minWidth: timelineMinWidth }}
  >
    <TimelineMonthHeader
      monthTicks={monthTicks}
      domain={domain}
      spansMultipleYears={spansMultipleYears}
    />
    <TimelineWeekHeader weekSegments={weekSegments} domain={domain} />
    {rows.map((row) => (
      <TimelineBarRow
        key={row.id}
        row={row}
        domain={domain}
        monthTicks={monthTicks}
        weekTicks={weekTicks}
      />
    ))}
    <div
      className="pointer-events-none absolute inset-y-0 w-px bg-red-500"
      style={{ left: `${todayPct}%` }}
      aria-hidden
    />
  </div>
);

// --- TimelineChart (public) ---

interface TimelineChartProps {
  rows: MilestoneRow[];
  domain: { start: Date; end: Date };
  monthTicks: Date[];
  weekTicks: Date[];
  weekSegments: WeekSegment[];
  spansMultipleYears: boolean;
  timelineMinWidth: number;
  todayPct: number;
}

export const TimelineChart: FC<TimelineChartProps> = ({
  rows,
  domain,
  monthTicks,
  weekTicks,
  weekSegments,
  spansMultipleYears,
  timelineMinWidth,
  todayPct,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex overflow-x-auto bg-white">
      <MilestoneTable rows={rows} t={t} />
      <TimelineBars
        rows={rows}
        domain={domain}
        monthTicks={monthTicks}
        weekTicks={weekTicks}
        weekSegments={weekSegments}
        spansMultipleYears={spansMultipleYears}
        timelineMinWidth={timelineMinWidth}
        todayPct={todayPct}
      />
    </div>
  );
};
